import { h, HarmajaOutput, ListView } from "harmaja"
import _ from "lodash"
import * as L from "lonna"
import { Board, Connection, findItem, Id, Item } from "../../../../common/src/domain"
import { Dispatch } from "../../store/board-store"
import { BoardFocus, getSelectedConnections, getSelectedItems } from "../board-focus"
import { Rect } from "../../../../common/src/geometry"
import { alignmentsMenu } from "./alignments"
import { areaTilingMenu } from "./areaTiling"
import { colorsAndShapesMenu } from "./colorsAndShapes"
import { fontSizesMenu } from "./fontSizes"
import { resolveEndpoint } from "../../../../common/src/connection-utils"
import { connectionEndsMenu } from "./connection-ends"
import { textAlignmentsMenu } from "./textAlignments"
import { lockMenu } from "./lock"
import { zOrderMenu } from "./zOrder"
import { votesMenu } from "./votes"
import { tableMenu } from "./table"
import { groupMenu } from "./group"
import { hideContentsMenu } from "./hideContents"
import { textFormatsMenu } from "./textFormats"
import { nonNull } from "../board-scroll-and-zoom"
import { Icon16 } from "../../components/Icon16"
import { NOTE_COLORS, TRANSPARENT } from "../../../../common/src/colors"
import { isColoredItem, isTextItem } from "../../../../common/src/domain"
import { canChangeShapeAndColor } from "../board-permissions"
import { colorsSubMenu } from "./colors"
import { dispatchDeletion } from "../item-delete"
import { dispatchDuplication } from "../item-duplicate"
import { CRDTStore } from "../../store/crdt-store"
import { shapesSubMenu } from "./shapes"

export type SubmenuProps = {
    focusedItems: L.Property<{ items: Item[]; connections: Connection[] }>
    board: L.Property<Board>
    dispatch: Dispatch
    submenu: L.Atom<SubMenuCreator | null>
}

export type SubMenuCreator = (props: SubmenuProps) => HarmajaOutput

export const connectionPos = (b: Board | Record<string, Item>) => (c: Connection): Rect => {
    if (c.controlPoints.length === 0) {
        const start = resolveEndpoint(c.from, b)
        const end = resolveEndpoint(c.to, b)
        return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2, width: 0, height: 0 }
    }
    const p = c.controlPoints[0]
    return { ...p, width: 0, height: 0 }
}

type ItemsAndConnections = { items: Item[]; connections: Connection[] }
type Bounds = { minX: number; maxX: number; minY: number; maxY: number } | null

const getSelectionBounds = (items: ItemsAndConnections, b: Board): Bounds => {
    if (items.items.length === 0 && items.connections.length === 0) {
        return null
    }
    const rects = [...items.items, ...items.connections.map(connectionPos(b))]
    const minY = _.min(rects.map((i) => i.y)) || 0
    const minX = _.min(rects.map((i) => i.x)) || 0
    const maxY = _.max(rects.map((i) => i.y + i.height)) || 0
    const maxX = _.max(rects.map((i) => i.x + i.width)) || 0
    return { minX, maxX, minY, maxY }
}

const getStyleAndClass = (bounds: Bounds) => (vr: Rect) => {
    const cn = "context-menu-positioner"
    if (bounds === null) {
        return {
            style: null,
            className: cn,
        }
    }
    const { minX, maxX, minY, maxY } = bounds
    const alignRight = minX > vr.x + vr.width / 2
    const topOfItem = minY - vr.y > vr.height / 3
    return {
        style: {
            left: alignRight ? undefined : `max(${minX}em, ${vr.x}em)`,
            right: alignRight ? `calc(100% - min(${maxX}em, ${vr.x + vr.width}em))` : undefined,
            top: topOfItem ? `${minY}em` : `${maxY}em`,
        },
        className: cn + (topOfItem ? " item-top" : " item-bottom"),
    }
}

export const ContextMenuView = ({
    dispatch,
    board,
    focus,
    viewRect,
    crdtStore,
}: {
    dispatch: Dispatch
    board: L.Property<Board>
    focus: L.Atom<BoardFocus>
    viewRect: L.Property<Rect>
    crdtStore: CRDTStore
}) => {
    const focusedItems = L.view(focus, board, (f, b) => {
        if (f.status === "dragging" || f.status === "connection-adding" || f.status === "adding")
            return { items: [], connections: [] }
        return { items: getSelectedItems(b)(f), connections: getSelectedConnections(b)(f) }
    })

    const submenu = L.atom<SubMenuCreator | null>(null)
    L.view(
        focusedItems,
        (items) => items.items[0],
        (i) => i?.id,
    ).forEach(() => submenu.set(null))

    const props = { board, focusedItems, dispatch, submenu }

    // Design handoff structure: top-level = color swatches | Aa (text style) | lock | overflow
    const coloredItems = L.view(focusedItems, (f) => f.items.filter(isColoredItem))
    const hasColored = L.view(coloredItems, (i) => i.length > 0)
    const hasText = L.view(focusedItems, (f) => f.items.some(isTextItem))
    const hasAnything = L.view(focusedItems, (f) => f.items.length + f.connections.length > 0)
    const setColor = (color: string) => {
        dispatch({
            action: "item.update",
            boardId: board.get().id,
            items: coloredItems
                .get()
                .filter(canChangeShapeAndColor)
                .map((i) => ({ id: i.id, color })),
        })
    }
    const aaOpen = L.atom(false)
    const moreOpen = L.atom(false)
    focusedItems.pipe(L.changes).forEach(() => {
        aaOpen.set(false)
        moreOpen.set(false)
    })

    const aaWidgets = L.view(
        L.combineAsArray([
            fontSizesMenu(props),
            textFormatsMenu(props),
            textAlignmentsMenu(props),
            colorsSubMenu(props),
            shapesSubMenu(props),
        ]),
        (arrays) => arrays.flat().filter(nonNull),
    )
    const moreWidgets = L.view(
        L.combineAsArray([
            votesMenu(props),
            zOrderMenu(props),
            groupMenu(props),
            alignmentsMenu("x", props),
            alignmentsMenu("y", props),
            areaTilingMenu(props),
            hideContentsMenu(props),
            connectionEndsMenu(props),
            tableMenu(props),
        ]),
        (arrays) => arrays.flat().filter(nonNull),
    )

    const captureEvents = (e: JSX.MouseEvent) => {
        e.stopPropagation()
    }
    return L.view(hasAnything, (show) => {
        if (!show) return null
        const bounds = L.view(focus, () => getSelectionBounds(focusedItems.get(), board.get()))
        const styleAndClass = L.view(viewRect, bounds, (vr, b) => getStyleAndClass(b)(vr))

        return (
            <div
                className={L.view(styleAndClass, "className")}
                style={L.view(styleAndClass, "style")}
                onDoubleClick={captureEvents}
                onClick={captureEvents}
            >
                <div className="context-menu">
                    {L.view(hasColored, (c) =>
                        c ? (
                            <div className="inline-colors icon-group">
                                {NOTE_COLORS.filter((c) => c.color !== TRANSPARENT).map((c) => (
                                    <span
                                        className="mini-swatch"
                                        style={{ background: c.color }}
                                        title={c.name}
                                        onClick={() => setColor(c.color)}
                                    />
                                ))}
                            </div>
                        ) : null,
                    )}
                    {L.view(hasText, (t) =>
                        t ? (
                            <span
                                className={L.view(aaOpen, (o) => (o ? "menu-btn active" : "menu-btn"))}
                                title="Text style"
                                onClick={() => {
                                    moreOpen.set(false)
                                    aaOpen.modify((o) => !o)
                                }}
                            >
                                Aa
                            </span>
                        ) : null,
                    )}
                    <ListView
                        observable={L.view(lockMenu(props), (w) => w.filter(nonNull))}
                        renderItem={(x) => x}
                        getKey={(x) => x}
                    />
                    <span
                        className={L.view(moreOpen, (o) => (o ? "menu-btn active" : "menu-btn"))}
                        title="More actions"
                        onClick={() => {
                            aaOpen.set(false)
                            moreOpen.modify((o) => !o)
                        }}
                    >
                        <Icon16 name="overflow" />
                    </span>
                </div>
                {L.view(aaOpen, (o) =>
                    o ? (
                        <div className="submenu">
                            <ListView observable={aaWidgets} renderItem={(x) => x} getKey={(x) => x} />
                        </div>
                    ) : null,
                )}
                {L.view(moreOpen, (o) =>
                    o ? (
                        <div className="submenu overflow-menu">
                            <ListView observable={moreWidgets} renderItem={(x) => x} getKey={(x) => x} />
                            <div className="action-rows">
                                <span
                                    className="action"
                                    onClick={() => {
                                        moreOpen.set(false)
                                        dispatchDuplication(focus, board.get(), dispatch, crdtStore)
                                    }}
                                >
                                    <Icon16 name="duplicate" /> Duplicate
                                </span>
                                <span
                                    className="action destructive"
                                    onClick={() => {
                                        moreOpen.set(false)
                                        dispatchDeletion(board.get().id, focus.get(), dispatch)
                                    }}
                                >
                                    <Icon16 name="trash" /> Delete
                                </span>
                            </div>
                        </div>
                    ) : null,
                )}
                {L.view(submenu, (show) => (show ? show(props) : null))}
            </div>
        )
    })
}
